import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout, ConfigProvider, Alert, Modal, Button } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import 'antd/dist/antd.css';
import './App.css';
import './styles/typography.css';
import './styles/appSearch.css';
import './styles/responsive.css';
import { RBACService } from './utils/rbac';
import { getLoginRedirectUrl } from './utils/authRedirect';
import { shouldRedirectToSuperAdmin, getSuperAdminRedirectUrl } from './utils/subdomainRedirect';

// Components
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRedirect from './components/RoleBasedRedirect';

// Pages
import Dashboard from './pages/Dashboard';
import Sales from './pages/Sales';
import POS from './pages/POS';
import PurchaseOrders from './pages/PurchaseOrders';
import GRPO from './pages/GRPO';
import Inventory from './pages/Inventory';
import Products from './pages/Products';
import ProductExcelImport from './pages/ProductExcelImport';
import Vendors from './pages/Vendors';
import VendorProducts from './pages/VendorProducts';
import Reports from './pages/Reports';
import Documents from './pages/Documents';
import Minesweeper from './pages/Minesweeper';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminRouter from './components/superadmin/SuperAdminRouter';
import AdminPortal from './pages/AdminPortal';
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import SolutionsPage from './pages/SolutionsPage';
import SupportPage from './pages/SupportPage';
import BlogPage from './pages/BlogPage';
import TermsOfUse from './pages/TermsOfUse';
import BillingPayment from './pages/BillingPayment';
import ResetPassword from './pages/ResetPassword';

// RBAC
import { PERMISSIONS, INVENTORY_ACCESS_PERMISSIONS } from './utils/rbac';

const { Content } = Layout;

const RouteSideEffects: React.FC<{ onLoginRoute: () => void }> = ({ onLoginRoute }) => {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname || '';
    if (p.startsWith('/user/login') || p.startsWith('/admin/login') || p.startsWith('/superadmin/login')) {
      onLoginRoute();
    }
  }, [location.pathname, onLoginRoute]);
  return null;
};

/** Inter + type scale on app UI; off on marketing + login pages. */
const AppBodyFontClass: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname || '';
    const isPublicMarketing =
      p === '/' ||
      p === '/about' ||
      p === '/solutions' ||
      p === '/support' ||
      p === '/blog' ||
      p === '/terms' ||
      p === '/billing';
    const isLogin =
      p.startsWith('/user/login') ||
      p.startsWith('/admin/login') ||
      p.startsWith('/superadmin/login');

    document.body.classList.toggle('app-ui-fonts', !isPublicMarketing && !isLogin);

    // Keep Figma login styles scoped — never leak into portal/marketing.
    if (!isLogin) {
      document.documentElement.classList.remove('admin-login-active', 'user-login-active');
    }
  }, [location.pathname]);
  return null;
};

// Simple ErrorBoundary to show rendering issues instead of blank page
class ErrorBoundary extends React.Component<{ children?: React.ReactNode }, { hasError: boolean; error?: Error | null }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: any) {
    console.error('App caught error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24 }}>
          <h1>Something went wrong</h1>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{String(this.state.error)}</pre>
        </div>
      );
    }
    return this.props.children || null;
  }
}

const AuthWrapper: React.FC<any> = ({ children, requiredPermissions, requireAll, requiredRole }) => {
  return (
    <ProtectedRoute requiredPermissions={requiredPermissions} requireAll={requireAll} requiredRole={requiredRole}>
      {children}
    </ProtectedRoute>
  );
};

/** When a Super Admin is on the main app (e.g. /dashboard), send them to the Super Admin panel so they see Company Management. */
const MainAppGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = RBACService.getCurrentUser();
  if (user?.roleName?.toLowerCase() === 'super admin') {
    return <Navigate to="/superadmin" replace />;
  }
  return <>{children}</>;
};

/** Old /users and /locations bookmarks: company Admin → Admin Portal; everyone else → dashboard. */
const LegacyUsersRouteRedirect: React.FC = () => {
  const u = RBACService.getCurrentUser();
  if ((u?.roleName || '').toLowerCase() === 'admin') {
    return <Navigate to="/admin-portal/users" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};
const LegacyLocationsRouteRedirect: React.FC = () => {
  const u = RBACService.getCurrentUser();
  if ((u?.roleName || '').toLowerCase() === 'admin') {
    return <Navigate to="/admin-portal/locations" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

const App: React.FC = () => {
  console.log('[App] mounting - debug=', process.env.REACT_APP_DEBUG);
  
  // Clear debug authentication if debug mode is disabled
  useEffect(() => {
    const isDebugMode = process.env.REACT_APP_DEBUG === 'true';
    const token = localStorage.getItem('authToken');
    
    if (!isDebugMode && token === 'debug-token') {
      console.log('[App] Clearing debug authentication tokens...');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userRoleId');
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      RBACService.clearUser();
    }
  }, []);

  // Subdomain redirect: superadmin subdomain -> Super Admin login; main domain /superadmin/* -> superadmin subdomain
  useEffect(() => {
    const path = window.location.pathname || '';
    const onSuperAdminSubdomain = shouldRedirectToSuperAdmin();
    const superAdminRedirectUrl = getSuperAdminRedirectUrl();

    if (onSuperAdminSubdomain) {
      if (!path.startsWith('/superadmin')) {
        window.location.replace('/superadmin/login');
        return;
      }
    } else if (path.startsWith('/superadmin') && superAdminRedirectUrl) {
      window.location.replace(superAdminRedirectUrl);
      return;
    }
  }, []);

  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const wasMobileRef = useRef<boolean | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [sessionWarning, setSessionWarning] = useState<{ visible: boolean; minutesLeft: number }>({
    visible: false,
    minutesLeft: 0
  });
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width <= 768;
      setIsMobile(mobile);
      setIsSmallMobile(width <= 420);
      // Auto-close only when transitioning from desktop -> mobile.
      if (mobile && wasMobileRef.current === false) {
        setCollapsed(true);
      }
      wasMobileRef.current = mobile;
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Session handling
  useEffect(() => {
    const handleSessionWarning = (event: any) => {
      setSessionWarning({ visible: true, minutesLeft: event?.detail?.minutesLeft ?? 0 });
    };

    const handleSessionExpired = () => {
      const path = window.location.pathname || '';
      const isAdminPortal = path.startsWith('/admin-portal');
      const isSuperAdmin = path.startsWith('/superadmin') && !path.startsWith('/superadmin/login');
      if (isAdminPortal || isSuperAdmin) {
        setSessionWarning({ visible: false, minutesLeft: 0 });
        localStorage.removeItem('authToken');
        window.location.href = getLoginRedirectUrl(path);
        return;
      }
      setSessionExpired(true);
      setSessionWarning({ visible: false, minutesLeft: 0 });
    };

    const handleSessionRestored = () => {
      setSessionExpired(false);
      setSessionWarning({ visible: false, minutesLeft: 0 });
    };

    let lastExtendAt = 0;
    const handleActivity = () => {
      const now = Date.now();
      // Throttle to avoid writing localStorage on every mousemove.
      if (now - lastExtendAt < 10000) return;
      lastExtendAt = now;
      try {
        RBACService.extendSession();
      } catch (err) {
        console.warn('Failed to extend session', err);
      }
    };

    window.addEventListener('sessionWarning', handleSessionWarning);
    window.addEventListener('sessionExpired', handleSessionExpired);
    window.addEventListener('sessionRestored', handleSessionRestored as any);

    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity, { passive: true });
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('touchstart', handleActivity, { passive: true });

    return () => {
      window.removeEventListener('sessionWarning', handleSessionWarning);
      window.removeEventListener('sessionExpired', handleSessionExpired);
      window.removeEventListener('sessionRestored', handleSessionRestored as any);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, []);

  const handleSidebarCollapse = (coll: boolean) => setCollapsed(coll);

  useEffect(() => {
    if (isMobile && wasMobileRef.current === false) {
      setCollapsed(true);
    }
  }, [isMobile]);

  // Body class for form modals: align full-area form to content (sidebar width)
  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    return () => document.body.classList.remove('sidebar-collapsed');
  }, [collapsed]);

  // Debug logs so you can verify App & routes load
  useEffect(() => {
    console.log('[App] active routes:', [
      '/dashboard',
      '/sales',
      '/products',
      '/inventory',
      '/purchase-orders',
      '/reports',
      '/documents',
      '/minesweeper'
    ]);
    console.log('[App] token:', localStorage.getItem('access_token') ? 'present' : 'missing');
  }, []);

  return (
    <ConfigProvider>
      <Router>
        <AppBodyFontClass />
        <RouteSideEffects
          onLoginRoute={() => {
            setSessionExpired(false);
            setSessionWarning({ visible: false, minutesLeft: 0 });
          }}
        />
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/billing" element={<BillingPayment />} />
            <Route path="/login" element={<Navigate to="/user/login" replace />} />
            <Route path="/user/login" element={<Login />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/superadmin/login" element={<SuperAdminLogin />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* SuperAdmin routes - Super Admin role only */}
            <Route
              path="/superadmin/*"
              element={
                <AuthWrapper requiredRole="Super Admin">
                  <SuperAdminRouter />
                </AuthWrapper>
              }
            />

            {/* Admin Portal is Admin-only; non-admin roles must use the main user portal */}
            <Route
              path="/admin-portal/*"
              element={
                <AuthWrapper requiredRole="Admin">
                  <AdminPortal />
                </AuthWrapper>
              }
            />

            <Route
              path="/*"
              element={
                <AuthWrapper>
                  <MainAppGuard>
                  <Layout className="user-portal-shell" style={{ overflow: 'hidden', display: 'flex' }}>
                    <Sidebar collapsed={collapsed} onCollapse={handleSidebarCollapse} isMobile={isMobile} />
                    <Layout
                      className={`site-layout ${
                        isMobile ? 'mobile-layout' : collapsed ? 'site-layout-collapsed' : ''
                      } ${isMobile && !collapsed ? 'sidebar-drawer-open' : ''} ${isSmallMobile ? 'mobile-layout-xs' : ''}`}
                      style={{
                        marginLeft: isMobile ? 0 : (collapsed ? 80 : 280),
                        width: isMobile ? '100%' : `calc(100% - ${collapsed ? 80 : 280}px)`,
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Header isMobile={isMobile} sidebarCollapsed={collapsed} onOpenSidebar={() => setCollapsed(prev => !prev)} />
                      <Content
                        className="main-content-full user-portal-content"
                        style={{
                          margin: 0,
                          padding: isMobile ? (isSmallMobile ? 8 : 12) : 24,
                          paddingTop: isMobile ? (isSmallMobile ? 8 : 12) : 16,
                          flex: 1,
                          minHeight: 0,
                          height: '100%',
                          backgroundColor: '#fff',
                          borderRadius: 0,
                          width: '100%',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          zIndex: 0,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        {/* Session warning modal */}
                        <Modal
                          title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
                              Session Timeout Warning
                            </div>
                          }
                          visible={sessionWarning.visible}
                          onCancel={() => setSessionWarning({ visible: false, minutesLeft: 0 })}
                          footer={[
                            <Button
                              key="extend"
                              type="primary"
                              onClick={() => {
                                try {
                                  RBACService.extendSession();
                                } catch (err) {
                                  console.warn('extendSession failed', err);
                                }
                                setSessionWarning({ visible: false, minutesLeft: 0 });
                              }}
                            >
                              Extend Session
                            </Button>,
                            <Button
                              key="logout"
                              onClick={() => {
                                window.location.href = getLoginRedirectUrl();
                              }}
                            >
                              Logout Now
                            </Button>
                          ]}
                          centered
                        >
                          <p>
                            Your session will expire in {sessionWarning.minutesLeft} minute
                            {sessionWarning.minutesLeft !== 1 ? 's' : ''}.
                          </p>
                          <p>Would you like to extend your session or logout now?</p>
                        </Modal>

                        {/* Session expired modal */}
                        <Modal
                          title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                              Session Expired
                            </div>
                          }
                          visible={sessionExpired}
                          closable={false}
                          footer={[
                            <Button
                              key="login"
                              type="primary"
                              onClick={() => {
                                setSessionExpired(false);
                                window.location.href = getLoginRedirectUrl();
                              }}
                            >
                              Go to Login
                            </Button>
                          ]}
                          centered
                        >
                          <p>Your session has expired due to inactivity.</p>
                          <p>Please log in again to continue.</p>
                        </Modal>

                        {permissionError && (
                          <Alert
                            message="Access Denied"
                            description={permissionError}
                            type="error"
                            closable
                            onClose={() => setPermissionError(null)}
                            style={{ marginBottom: 16 }}
                          />
                        )}

                        <div className="main-content-inner" style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                        <Routes>
                          <Route 
                            path="/" 
                            element={
                              <RoleBasedRedirect />
                            } 
                          />

                          <Route
                            path="/dashboard"
                            element={
                              <AuthWrapper requiredPermissions={[PERMISSIONS.DASHBOARD_VIEW]} requireAll={false}>
                                <Dashboard />
                              </AuthWrapper>
                            }
                          />

                          <Route path="/users" element={<LegacyUsersRouteRedirect />} />
                          <Route path="/locations" element={<LegacyLocationsRouteRedirect />} />

                          <Route
                            path="/sales"
                            element={
                              <AuthWrapper
                                requiredPermissions={[PERMISSIONS.POS_MANAGE, PERMISSIONS.REPORT_VIEW]}
                                requireAll={false}
                              >
                                <Sales />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/pos"
                            element={
                              <AuthWrapper
                                requiredPermissions={[PERMISSIONS.POS_MANAGE]}
                                requireAll
                              >
                                <POS />
                              </AuthWrapper>
                            }
                          />
                          <Route
                            path="/pos/cart"
                            element={
                              <AuthWrapper
                                requiredPermissions={[PERMISSIONS.POS_MANAGE]}
                                requireAll
                              >
                                <POS />
                              </AuthWrapper>
                            }
                          />
                          <Route
                            path="/pos/stashed"
                            element={
                              <AuthWrapper
                                requiredPermissions={[PERMISSIONS.POS_MANAGE]}
                                requireAll
                              >
                                <POS />
                              </AuthWrapper>
                            }
                          />
                          <Route
                            path="/pos/transactions"
                            element={
                              <AuthWrapper
                                requiredPermissions={[PERMISSIONS.POS_MANAGE]}
                                requireAll
                              >
                                <POS />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/purchase-orders"
                            element={
                              <AuthWrapper
                                requiredPermissions={[
                                  PERMISSIONS.PO_CREATE,
                                  PERMISSIONS.PO_READ,
                                  PERMISSIONS.PO_UPDATE,
                                  PERMISSIONS.PO_DELETE
                                ]}
                                requireAll={false}
                              >
                                <PurchaseOrders />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/grpo"
                            element={
                              <AuthWrapper
                                requiredPermissions={[
                                  PERMISSIONS.GOODS_RECEIVE,
                                ]}
                                requireAll={false}
                              >
                                <GRPO />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/inventory"
                            element={
                              <AuthWrapper
                                requiredPermissions={INVENTORY_ACCESS_PERMISSIONS}
                                requireAll={false}
                              >
                                <Inventory />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/products"
                            element={
                              <AuthWrapper
                                requiredPermissions={[
                                  PERMISSIONS.PRODUCT_CREATE,
                                  PERMISSIONS.PRODUCT_READ,
                                  PERMISSIONS.PRODUCT_UPDATE,
                                  PERMISSIONS.PRODUCT_DELETE
                                ]}
                                requireAll={false}
                              >
                                <Products />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/products/import-excel"
                            element={
                              <AuthWrapper requiredPermissions={[PERMISSIONS.PRODUCT_CREATE]} requireAll={false}>
                                <ProductExcelImport />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/vendor-products"
                            element={
                              <AuthWrapper
                                requiredPermissions={[
                                  PERMISSIONS.VENDOR_CREATE,
                                  PERMISSIONS.VENDOR_READ,
                                  PERMISSIONS.VENDOR_UPDATE,
                                  PERMISSIONS.VENDOR_DELETE,
                                  PERMISSIONS.VENDOR_PRODUCT_MANAGE,
                                ]}
                                requireAll={false}
                              >
                                <VendorProducts />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/vendors"
                            element={
                              <AuthWrapper
                                requiredPermissions={[
                                  PERMISSIONS.VENDOR_CREATE,
                                  PERMISSIONS.VENDOR_READ,
                                  PERMISSIONS.VENDOR_UPDATE,
                                  PERMISSIONS.VENDOR_DELETE,
                                  PERMISSIONS.VENDOR_PRODUCT_MANAGE,
                                ]}
                                requireAll={false}
                              >
                                <Vendors />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/documents"
                            element={
                              <AuthWrapper
                                requiredPermissions={[
                                  PERMISSIONS.DOCUMENT_UPLOAD,
                                  PERMISSIONS.DOCUMENT_READ,
                                  PERMISSIONS.DOCUMENT_DELETE
                                ]}
                                requireAll={false}
                              >
                                <Documents />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/reports"
                            element={
                              <AuthWrapper
                                requiredPermissions={[PERMISSIONS.REPORT_VIEW, PERMISSIONS.REPORT_EXPORT]}
                                requireAll={false}
                              >
                                <Reports />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/minesweeper"
                            element={
                              <AuthWrapper>
                                <Minesweeper />
                              </AuthWrapper>
                            }
                          />

                        </Routes>
                        </div>
                      </Content>
                    </Layout>
                  </Layout>
                  </MainAppGuard>
                </AuthWrapper>
              }
            />
          </Routes>
        </ErrorBoundary>
      </Router>
    </ConfigProvider>
  );
};

export default App;